import { AxiosRequestHeaders } from 'axios';
import { Provider, ProviderCategory, ProviderDetails, ProviderType } from '~/types';
import { getData, patchData, postData } from '../../../utils/service';
import { OptionType, ProviderType_, TypeAndCategoryValues, WelcomeValues } from './types';

export const PROVIDER_TYPES_ROUTE = {
  get: '/provider-types{provider_category_id}',
};
export const PROVIDER_CATEGORIES_ROUTE = {
  get: '/provider-categories',
};
export const PROVIDER_DETAILS_ROUTE = {
  get: '/provider-details',
};

export const loadTypeOptions = async (categoryId: number) => {
  const data = await getData<ProviderType[]>('providerTypes', {
    pathParams: { provider_category_id: categoryId ? `/${categoryId}` : '' },
  });
  return data.map((item: ProviderType) => ({
    value: item.id,
    label: item.providerTypeCode,
  }));
};

export const loadCategoryOptions = async (): Promise<OptionType[]> => {
  const data = await getData<ProviderCategory[]>('providerCategories');
  return data.map((item: ProviderCategory) => ({
    value: item.id,
    label: item.providerCategoryCode,
    npiFlag: item.npiFlag,
  }));
};

export const getProviderDetails = async (): Promise<ProviderDetails> => {
  return await getData('providerDetails');
};

export const DOCUMENTS_ROUTES = {
  get: '/documents/',
  post: '/documents',
  patch: '/documents/{id}',
  delete: '/documents/{id}',
};
export const DOCUMENT_DOWNLOAD_ROUTES = {
  get: '/documents/{id}/content',
};
/**
 * Fetch all provider categories
 */
export const fetchCategories = async (): Promise<ProviderCategory[]> => {
  return await getData<ProviderCategory[]>('providerCategories');
};

/**
 * Fetch provider types by category ID
 */
export const fetchTypes = async (): Promise<ProviderType_[]> => {
  return await getData<ProviderType_[]>('allProviderTypes', {});
};

/**
 * Update provider information
 */
export const creatreProvider = async (
  provider: Provider,
  data: TypeAndCategoryValues
): Promise<Provider> => {
  if (!provider?.id) {
    throw new Error('Provider ID is required');
  }

  return await patchData<Provider>('provider', {
    pathParams: { id: provider.id.toString() },
    data: {
      firstName: data.firstName,
      lastName: data.lastName,
      npiNum: data.npiNum,
      providerTypeId: data.type?.value,
      providerCategoryId: data.category?.value,
    },
  });
};

/**
 * Upload CV or Credentialing Packet. `docType` drives docCategory, workflow_type,
 * and docDesc so the document flows into the right parser.
 */
export const uploadCV = async (
  providerId: number | null,
  file: File,
  docType: 'cv' | 'packet'
): Promise<{ id: number; docName: string }> => {
  if (!providerId) {
    throw new Error('Provider ID is required');
  }

  const isPacket = docType === 'packet';
  // MCV-1425: packets file under Licensing (they carry license-related
  // credentials); CVs land in Employment & Work History.
  const docCategory = isPacket ? 'licensing' : 'employment_and_work_history';
  const workflowType = isPacket ? 'packet-parse' : 'cv-parse';
  const docDesc = isPacket
    ? '[System] Credentialing Packet Upload & Parse during registration'
    : '[System] CV Upload & Parse during registration';

  const formData = new FormData();
  formData.append('file', file);
  formData.append('providerId', providerId.toString());
  formData.append('docCategory', docCategory);
  formData.append('docDesc', docDesc);
  formData.append('docType', docType);
  formData.append('docName', file.name || '');

  return await postData<{ id: number; docName: string }>('documents', {
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    } as unknown as AxiosRequestHeaders,
    params: {
      workflow_type: workflowType,
    },
    timeout: 600000,
  });
};

/**
 * Terminal AI job statuses, mirroring TERMINAL_JOB_STATUSES in
 * apps/mcv-api/mcv_api/routes/document/controllers.py.
 */
const PARSE_SUCCESS_STATUSES = ['COMPLETED', 'COMPLETE'];
const PARSE_FAILURE_STATUSES = ['FAILED', 'ERROR'];

export type ParseStatus = 'pending' | 'complete' | 'failed';

interface DocumentWithAiJobs {
  id: number;
  aiJobs?: { job_status?: string }[];
}

/**
 * Poll the parse status for one uploaded document.
 *
 * Registration cannot use the `ai-job.user` websocket: the socket is only
 * spawned once userState reaches $_AUTHENTICATED, which is the moment
 * registration COMPLETES, and those messages route solely to the vault's
 * documentsService. So the wizard asks the API directly.
 *
 * `GET /documents/with-results` already returns each document with its AI jobs
 * scoped to the requesting user, so no new endpoint is needed.
 */
export const fetchParseStatus = async (
  providerId: number,
  documentId: number
): Promise<ParseStatus> => {
  const documents = await getData<DocumentWithAiJobs[]>('documents', {
    params: { providerId },
  });

  const jobs = documents.find((doc) => doc.id === documentId)?.aiJobs ?? [];
  // The API orders jobs newest-first; a re-parse would add a newer one.
  const status = jobs[0]?.job_status?.toUpperCase();

  if (!status) return 'pending';
  if (PARSE_SUCCESS_STATUSES.includes(status)) return 'complete';
  if (PARSE_FAILURE_STATUSES.includes(status)) return 'failed';
  return 'pending';
};

/**
 * Create user role
 */
export const createUserRole = async (
  userInfoId: number
): Promise<{ userId: number; roleCode: string }> => {
  const payload = {
    userId: userInfoId,
    roleCode: 'PROVIDER',
  };
  return postData<{ userId: number; roleCode: string }>('userRole', {
    data: payload,
  });
};

interface CreateReq {
  userInfoId: number;
  typeAndCategory: TypeAndCategoryValues;
  welcome: WelcomeValues;
}
/**
 * Create Provider
 */
export const createProvider = async (data: CreateReq): Promise<Partial<Provider>> => {
  const payload = {
    firstName: data.typeAndCategory.firstName,
    lastName: data.typeAndCategory.lastName,
    npiNum: data.typeAndCategory.npiNum || undefined,
    userInfoId: data.userInfoId,
    providerTypeId: data.typeAndCategory.type?.value,
    providerCategoryId: data.typeAndCategory.category?.value,
    email: data.welcome.email,
    phone: data.welcome.phone,
    mfaEnabled: data.welcome.mfa,
    tosAccepted: data.welcome.tosAccepted,
    publications: [],
  };
  return postData<Partial<Provider>>('provider', {
    data: payload,
  });
};
